import { Dispatch, SetStateAction, useCallback } from 'react';
import { RemovePIIReportEvents } from 'src/components/pages/Case/Case.slice';
import { DataDeletionType } from 'src/graphql/generated';
import { FieldWithTextRange } from '../TextDeletionInput/TextDeletionInput';

interface UseTextFieldClearAllProps {
  setPayload: Dispatch<SetStateAction<DataDeletionType>>;
  reportEvents: RemovePIIReportEvents[];
  findEventDeletionIndex: (
    eventDeletions: Array<{ eventId: string }>,
    eventId: string,
  ) => number;
}

export function useTextFieldClearAll({
  setPayload,
  reportEvents,
  findEventDeletionIndex,
}: UseTextFieldClearAllProps) {
  return useCallback(
    (field: FieldWithTextRange, eventId?: string) => {
      setPayload((prevPayload: DataDeletionType) => {
        if (eventId && reportEvents && reportEvents.length >= 1) {
          // Event-scoped mode: clear field in specific event
          const existingEventDeletions = prevPayload.eventDeletions || [];
          const eventIndex = findEventDeletionIndex(
            existingEventDeletions,
            eventId,
          );

          if (eventIndex >= 0) {
            const updatedEventDeletions = [...existingEventDeletions];
            const currentEvent = updatedEventDeletions[eventIndex];

            // Only clear fields that exist in EventDeletionType
            if (field === 'description') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                description: [],
              };
            } else if (field === 'emotion') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                emotion: [],
              };
            } else if (field === 'witnesses') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                witnesses: [],
              };
            } else if (field === 'location') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                location: [],
              };
            } else if (field === 'externalNamedOffenders') {
              updatedEventDeletions[eventIndex] = {
                ...currentEvent,
                externalNamedOffenders: [],
              };
            }

            return {
              ...prevPayload,
              eventDeletions: updatedEventDeletions,
            };
          }
        } else {
          // Legacy mode: clear report-wide field
          return {
            ...prevPayload,
            [field]: [],
          };
        }
        return prevPayload;
      });
    },
    [setPayload, reportEvents, findEventDeletionIndex],
  );
}
